import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Grid,
  Link as MuiLink,
} from "@mui/material";
import {
  Publish,
  AttachFile,
  UploadFile,
  Create,
  Link as LinkIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { ThinLayout } from "../Layouts/ThinLayout";
import { BlingCheckmark } from "../Components/BlingCheckmark";
import { FileAttachment } from "../Components/FileUpload/FileAttachment";
import { DocumentViewer } from "../Components/SubmissionReview/DocumentViewer";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_SUBMISSION_REVIEW_SUBMISSION,
  SUBMIT_SUBMISSION_REVIEW_SUBMISSION,
  SAVE_SUBMISSION_DRAFT,
  GET_SUBMISSION_REVIEW_UPLOAD_URL,
  CONFIRM_SUBMISSION_REVIEW_UPLOAD,
} from "../Queries";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { extractErrorMessage } from "../utils/errorUtils";
import { useTranslation } from "react-i18next";

export function StudentSubmissionPage() {
  const [searchParams] = useSearchParams();
  const { setError } = useErrorBoundary();
  const [t] = useTranslation();
  const [submissionText, setSubmissionText] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  // Written submissions are either/or: upload a finished document OR write
  // inline. Students doing both left faculty unsure which copy was
  // definitive, so the form makes them pick a path (switchable).
  const [entryMode, setEntryMode] = useState<"upload" | "write" | null>(null);

  // Decode token from query params
  const key = searchParams.get("key");
  let submissionId = "";
  let submissionToken = "";
  try {
    const decoded = atob(key || "");
    const parts = decoded.split(":");
    submissionId = parts[0];
    submissionToken = parts.slice(1).join(":");
  } catch {
    // Invalid key
  }

  const authHeaders = {
    headers: { Authorization: `JWT ${submissionToken}` },
  };

  const { loading, data } = useQuery(GET_SUBMISSION_REVIEW_SUBMISSION, {
    variables: { pk: submissionId },
    context: authHeaders,
    skip: !submissionId,
    onCompleted: (result) => {
      const sub = result?.submissionReviewSubmissionById;
      if (sub?.submissionText) {
        setSubmissionText(sub.submissionText);
      }
      // Resume where the draft left off
      if (sub?.submissionFileUrl) {
        setEntryMode("upload");
      } else if (sub?.submissionText) {
        setEntryMode("write");
      }
    },
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error loading submission"))),
        error,
      ),
  });

  const [submitMutation] = useMutation(SUBMIT_SUBMISSION_REVIEW_SUBMISSION, {
    context: authHeaders,
    refetchQueries: [
      {
        query: GET_SUBMISSION_REVIEW_SUBMISSION,
        variables: { pk: submissionId },
        context: authHeaders,
      },
    ],
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error submitting"))),
        error,
      ),
  });

  const [saveDraftMutation] = useMutation(SAVE_SUBMISSION_DRAFT, {
    context: authHeaders,
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error saving draft"))),
        error,
      ),
  });

  const [getUploadUrl] = useMutation(GET_SUBMISSION_REVIEW_UPLOAD_URL, {
    context: authHeaders,
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message);
    },
  });

  const [confirmUpload] = useMutation(CONFIRM_SUBMISSION_REVIEW_UPLOAD, {
    context: authHeaders,
    onCompleted: () => {
      setFileUploading(false);
      setFileUploadError(null);
    },
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message);
    },
  });

  const submission = data?.submissionReviewSubmissionById;

  if (loading) {
    return (
      <ThinLayout title={String(t("Loading..."))} loading={true}>
        <div />
      </ThinLayout>
    );
  }

  if (!submission) {
    return (
      <ThinLayout title={String(t("Error"))}>
        <Typography variant="h5">{t("Submission not found")}</Typography>
      </ThinLayout>
    );
  }

  // Already submitted — show confirmation
  if (submission.status === "SB") {
    return (
      <ThinLayout title="">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 600 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("You're all done! Your submission has been received.")}
            </Typography>
            <BlingCheckmark
              active={false}
              completed={true}
              size="large"
              divProps={{ style: { marginTop: 20 } }}
            />
            {(submission.submissionText || submission.submissionFileUrl) && (
              <Card sx={{ mt: 4, textAlign: "left" }}>
                <CardHeader title={String(t("Your Submission"))} />
                <CardContent>
                  {submission.submissionText && (
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {submission.submissionText}
                    </Typography>
                  )}
                  {/* Show the attached file so students can confirm what
                      was actually submitted */}
                  {submission.submissionFileUrl && (
                    <Box
                      sx={{
                        mt: submission.submissionText ? 2 : 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <AttachFile fontSize="small" color="action" />
                      <MuiLink
                        href={submission.submissionFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("View your attached file")}
                      </MuiLink>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </ThinLayout>
    );
  }

  const handleSubmit = () => {
    submitMutation({
      variables: {
        submissionId: submission.id,
        // Upload path submits the document alone — never a stale draft text,
        // so faculty always know which copy is definitive.
        submissionText:
          isWritten && entryMode === "upload" ? "" : submissionText,
      },
    });
  };

  const handleGetUploadUrl = async (filename: string, fileSize: number) => {
    setFileUploading(true);
    setFileUploadError(null);
    const result = await getUploadUrl({
      variables: { submissionId: submission.id, filename, fileSize },
    });
    return result.data?.getSubmissionReviewUploadUrl;
  };

  const handleConfirmUpload = async (
    fileUrl: string,
    originalFilename: string,
  ) => {
    await confirmUpload({
      variables: { submissionId: submission.id, fileUrl, originalFilename },
    });
  };

  const hasFile = attachedFile !== null || submission?.submissionFileUrl;
  const hasText = submissionText.trim().length > 0;
  const submissionType = submission?.activitySubmissionType;

  // Written submissions (DOC; TXT is the legacy alias) let the student
  // choose exactly one path: upload or write inline.
  const isWritten = submissionType === "DOC" || submissionType === "TXT";
  const isVideo = submissionType === "VID";
  const isLink = submissionType === "LNK";
  const isValidUrl = /^https?:\/\/\S+\.\S+/.test(submissionText.trim());

  // Format-specific validation
  const canSubmit = (() => {
    if (isWritten) {
      return entryMode === "upload" ? Boolean(hasFile) : hasText;
    }
    if (isVideo) return Boolean(hasFile);
    if (isLink) return isValidUrl;
    return hasText || hasFile; // Default: either
  })();

  const isOverdue = Boolean(
    submission.activityDueDate &&
      new Date() > new Date(submission.activityDueDate),
  );
  const isClosed =
    submission.activityStatus !== "SO" ||
    Boolean(
      submission.activityCloseDate &&
        new Date() > new Date(submission.activityCloseDate),
    );

  return (
    <ThinLayout
      title={submission.activityName || String(t("Submit Your Work"))}
      fullWidth={true}
    >
      <Box sx={{ p: 3 }}>
        {/* Due date warning */}
        {isOverdue && !isClosed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("This submission is past due, but you can still submit.")}
          </Alert>
        )}
        {isClosed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(
              "Submissions are closed. You can no longer submit or modify your work.",
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left: Instructions + Reference documentation */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                {/* Activity instructions */}
                {submission.activityDescription && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      {t("Instructions")}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {submission.activityDescription}
                    </Typography>
                  </Box>
                )}

                {/* Due date display */}
                {submission.activityDueDate && (
                  <Typography
                    variant="body2"
                    color={isOverdue ? "error" : "text.secondary"}
                    sx={{ mb: 2 }}
                  >
                    {t("Due")}:{" "}
                    {new Date(submission.activityDueDate).toLocaleDateString()}{" "}
                    {t("at")}{" "}
                    {new Date(submission.activityDueDate).toLocaleTimeString()}
                    {isOverdue && ` (${String(t("OVERDUE"))})`}
                  </Typography>
                )}

                {/* Reference documentation (instructor's attached file) */}
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("Reference Documentation")}
                </Typography>
                {submission.activityPdfAttachmentUrl ? (
                  <DocumentViewer
                    fileUrl={submission.activityPdfAttachmentUrl}
                  />
                ) : (
                  <Typography color="text.secondary">
                    {t("No reference documentation provided.")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right: Submission form */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title={String(t("Your Submission"))} />
              <CardContent>
                {/* External link: a single validated URL field */}
                {isLink && (
                  <TextField
                    label={String(t("Link to your work"))}
                    placeholder="https://..."
                    type="url"
                    fullWidth
                    value={submissionText}
                    onChange={(e) => setSubmissionText(e.target.value)}
                    disabled={isClosed}
                    error={hasText && !isValidUrl}
                    helperText={String(
                      hasText && !isValidUrl
                        ? t(
                            "Enter a full link starting with http:// or https://",
                          )
                        : t(
                            "Paste a link to your work (e.g. a shared document or site).",
                          ),
                    )}
                    InputProps={{
                      startAdornment: (
                        <LinkIcon fontSize="small" sx={{ mr: 1 }} />
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                )}

                {/* Video: file upload only */}
                {isVideo && (
                  <FileAttachment
                    onFileChange={setAttachedFile}
                    onGetUploadUrl={handleGetUploadUrl}
                    onConfirmUpload={handleConfirmUpload}
                    onUploadError={(message) => {
                      setFileUploading(false);
                      setFileUploadError(message);
                    }}
                    disabled={isClosed}
                    acceptedFileTypes=".pdf,.doc,.docx,.txt,.ppt,.pptx"
                    maxFileSize={10}
                    uploading={fileUploading}
                    uploadError={fileUploadError}
                    existingFileUrl={submission?.submissionFileUrl}
                    autoUpload={true}
                  />
                )}

                {/* Written submission: choose ONE path — upload a finished
                    document or write inline. */}
                {isWritten && entryMode === null && (
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t(
                        "Choose one — upload a finished document, or write your submission here.",
                      )}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setEntryMode("upload")}
                        startIcon={<UploadFile />}
                        disabled={isClosed}
                        sx={{ flex: 1, py: 3, flexDirection: "column", gap: 1 }}
                      >
                        {t("Upload a document")}
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setEntryMode("write")}
                        startIcon={<Create />}
                        disabled={isClosed}
                        sx={{ flex: 1, py: 3, flexDirection: "column", gap: 1 }}
                      >
                        {t("Write it here")}
                      </Button>
                    </Box>
                  </Box>
                )}

                {isWritten && entryMode === "upload" && (
                  <Box>
                    <FileAttachment
                      onFileChange={setAttachedFile}
                      onGetUploadUrl={handleGetUploadUrl}
                      onConfirmUpload={handleConfirmUpload}
                      onUploadError={(message) => {
                        setFileUploading(false);
                        setFileUploadError(message);
                      }}
                      disabled={isClosed}
                      acceptedFileTypes=".pdf,.doc,.docx,.txt,.ppt,.pptx"
                      maxFileSize={10}
                      uploading={fileUploading}
                      uploadError={fileUploadError}
                      existingFileUrl={submission?.submissionFileUrl}
                      autoUpload={true}
                    />
                    {!isClosed && (
                      <Button
                        size="small"
                        onClick={() => setEntryMode("write")}
                        sx={{ mt: 1 }}
                      >
                        {t("Write it here instead")}
                      </Button>
                    )}
                  </Box>
                )}

                {isWritten && entryMode === "write" && (
                  <Box>
                    {/* A written submission can be many pages — give it a
                        generous, growing editor rather than a cramped box. */}
                    <TextField
                      label={String(t("Your submission"))}
                      placeholder={String(t("Write your submission here..."))}
                      multiline
                      minRows={16}
                      maxRows={40}
                      fullWidth
                      value={submissionText}
                      onChange={(e) => setSubmissionText(e.target.value)}
                      disabled={isClosed}
                    />
                    {!isClosed && (
                      <Button
                        size="small"
                        onClick={() => setEntryMode("upload")}
                        sx={{ mt: 1 }}
                      >
                        {t("Upload a document instead")}
                      </Button>
                    )}
                  </Box>
                )}

                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  {!isClosed && (
                    <>
                      {(!isWritten || entryMode === "write") && !isVideo && (
                        <Button
                          variant="outlined"
                          onClick={() =>
                            saveDraftMutation({
                              variables: {
                                submissionId: submission.id,
                                submissionText,
                              },
                            })
                          }
                          disabled={!submissionText.trim()}
                        >
                          {t("Save Draft")}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        startIcon={<Publish />}
                      >
                        {t("Submit")}
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ThinLayout>
  );
}
